import Bugsnag from '@bugsnag/js';
import React from 'react';
import BugsnagPluginReact from '@bugsnag/plugin-react'

import config from 'config/index';
import { createAlert } from 'services/alert-service';

const KNOWN_TYPES = ['tasks', 'milestones', 'chats', 'files'];

let errorAlert = null;

Bugsnag.start({
    apiKey: config.BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact(React)],
    onError: error => {
        if (!config.LOG_ERRORS) {
            return false;
        }

        if (
            error.unhandled &&
            (
                error.originalError?.message?.indexOf('Request failed with status code') === 0 ||
                error.originalError?.message?.indexOf('timeout of') === 0 ||
                error.originalError?.message?.indexOf('Network Error') === 0 ||
                error.originalError?.isCanceled
            )
        ) {
            return false;
        }

        return error;
    },
    appVersion: process.env.REACT_APP_VERSION
});

export const ErrorBoundary = Bugsnag.getPlugin('react');

export const setUserProps = props => {
    Bugsnag.setUser(props?.id, props?.email, props?.name);
};

export const logError = ({ type, error, customData }) => {
    if (!config.LOG_ERRORS) {
        return;
    }

    Bugsnag.notify(error, event => {
        if (customData) {
            event.addMetadata('customData', customData);
        }

        event.context = type;

        if (type !== 'general') {
            event.groupingHash = type;
        }
    });
}

export const handleServerError = (error, options = {}) => {
    if (!navigator.onLine) {
        return;
    }

    if (!error.silent && !options.silent) {
        if (errorAlert) {
            errorAlert.hide(0, false, true);
        }

        errorAlert = createAlert(getErrorType(error), getMessage(error), {
            duration: 7000
        });
    }

    throw error;
}


const getMessage = error => {
    if (error) {
        if (error.response && error.response.status) {
            switch (error.response.status) {
                case 404: {
                    const [, type] = error.config.url.split('/').reverse();
                    if (KNOWN_TYPES.includes(type)) {
                        return `${type.charAt(0).toUpperCase() + type.slice(1).slice(0, -1)} is no longer available, or you may not have access to it.`;
                    }
                    return 'This resource is no longer available, or you may not have access to it.';
                }

                case 409:
                    return (error.response && error.response.data && error.response.data.message) || 'Please fill all fields.';

                case 403:
                    return 'You don\'t have enough permissions.';

                default:
            }
        }

        if (error.response && error.response.data && error.response.data.message) {
            if (error.response.data.message.message && typeof error.response.data.message.message === 'string') {
                return error.response.data.message.message;
            }

            if (typeof error.response.data.message === 'string') {
                return error.response.data.message;
            }
        }

        if (error.response && error.response.message) {
            if (error.response.message.message && typeof error.response.message.message === 'string') {
                return error.response.message.message;
            }

            if (typeof error.response.message === 'string') {
                return error.response.message;
            }
        }

        if (error.config && error.config.method === 'get') {
            return 'Oops.. We can\'t get the resource at the moment.';
        }

        if (typeof error === 'string') {
            return error;
        }
    }

    return 'Oops.. Something happened and your action wasn\'t completed.';
}

const getErrorType = error => {
    if (error && error.response && error.response.status) {
        switch (error.response.status) {
            case 403:
            case 404:
                return 'default';

            default:
        }
    }

    return 'error';
}
